import React, { useState, useEffect } from "react";
import api from "../utils/api";
import { useNavigate } from "react-router-dom";
import { useBottomNavBar } from "../contexts/BottomNavBarContext";
import { useUser } from "../contexts/UserContext";
import { deleteAccount, generateAccountDeleteOTP } from "../utils/api";
import styles from "./PreferenceCenter.module.css";
import Loader from "../components/Loader";
import { Button } from "../components/Button";
import { Popup } from "../components/Popup";
import { UIUtils } from "../utils/uiUtils";
import PageMetadata from "../components/PageMetadata";
import { CustomCheckbox } from "../components/CustomCheckbox";

interface PreferenceCenterConfig {
  showMarketingEmail: boolean;
  showPushNotification: boolean;
  showAudioRecording: boolean;
  showLocationPermission: boolean;
}

interface PreferenceCenterProps {
  config: PreferenceCenterConfig;
}

const PreferenceCenter: React.FC<PreferenceCenterProps> = ({ config }) => {
  const [marketingEmail, setMarketingEmail] = useState(false);
  const [marketingSms, setMarketingSms] = useState(false);
  const [pushNotification, setPushNotification] = useState(false);
  const [audioRecording, setAudioRecording] = useState(false);
  const [locationPermission, setLocationPermission] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");

  // Account deletion popup states
  const [showDeleteAccountPopup, setShowDeleteAccountPopup] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [reason, setReason] = useState("");

  // OTP related states
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [otp, setOtp] = useState("");
  const [referenceId, setReferenceId] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const navigate = useNavigate();
  const { setIsVisible } = useBottomNavBar();
  const {
    updateUserData,
    user,
    userGoal,
    benefitsSummary,
    benefitsBreakdown,
    marketing_email_dnc_flag,
    marketing_sms_dnc_flag,
  } = useUser();

  useEffect(() => {
    setIsVisible(true);
    return () => setIsVisible(false);
  }, [setIsVisible]);

  useEffect(() => {
    // Initialize marketing email preference from UserContext using the correct property
    if (marketing_email_dnc_flag !== undefined) {
      setMarketingEmail(!marketing_email_dnc_flag);
    }
  }, [marketing_email_dnc_flag]);

  useEffect(() => {
    // Initialize marketing SMS preference from UserContext
    if (marketing_sms_dnc_flag !== undefined) {
      setMarketingSms(!marketing_sms_dnc_flag);
    }
  }, [marketing_sms_dnc_flag]);

  // Start countdown timer for OTP resend
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCountdown > 0) {
      timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
    } else {
      setResendDisabled(false);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [resendCountdown]);

  // Account deletion functions
  const handleRequestOTP = async () => {
    if (!isConfirmed) {
      setSubmitError("Please confirm that you want to delete your account");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setOtpError("");

    try {
      // Request OTP for account deletion
      const response = await generateAccountDeleteOTP({
        email: user?.email || "",
      });

      // Check if the response contains an error status
      if (response.data.status === "error") {
        setSubmitError("Failed to send OTP. Please try again.");
        return;
      }

      if (response.data.status === "success") {
        console.log("OTP request successful, response:", response.data);
        setReferenceId(response.data.reference_id);
        setOtpSent(true);
        setShowOtpForm(true);

        // Start countdown for resend button
        setResendDisabled(true);
        setResendCountdown(60); // 60 seconds countdown

        // Track OTP request
        UIUtils.trackButtonClick("Account Deletion OTP Request");
        console.log(
          "OTP form should now be visible, otpSent:",
          true,
          "showOtpForm:",
          true,
        );
      } else {
        console.log("OTP request failed, response:", response.data);
        setSubmitError("Failed to send OTP. Please try again.");
      }
    } catch (error: any) {
      setSubmitError(
        "There was an error sending the verification code. Please try again later.",
      );
      UIUtils.trackException("Account deletion OTP request", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendDisabled) return;

    setIsSubmitting(true);
    setOtpError("");

    try {
      // Request new OTP
      const response = await generateAccountDeleteOTP({
        email: user?.email || "",
      });

      // Check if the response contains an error status
      if (response.data.status === "error") {
        setOtpError("Failed to resend verification code. Please try again.");
        return;
      }

      if (response.data.status === "success") {
        setReferenceId(response.data.reference_id);

        // Start countdown for resend button
        setResendDisabled(true);
        setResendCountdown(60); // 60 seconds countdown

        // Show success message
        setOtpError("Verification code resent successfully.");

        // Track OTP resend
        UIUtils.trackButtonClick("Account Deletion OTP Resend");
      } else {
        setOtpError("Failed to resend verification code. Please try again.");
      }
    } catch (error: any) {
      setOtpError(
        "There was an error resending the verification code. Please try again later.",
      );
      UIUtils.trackException("Account deletion OTP resend", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!isConfirmed) {
      setSubmitError("Please confirm that you want to delete your account");
      return;
    }

    // If OTP hasn't been requested yet, request it first
    if (!otpSent) {
      await handleRequestOTP();
      return;
    }

    // Validate OTP
    if (!otp) {
      setOtpError("Please enter the verification code");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setOtpError("");

    try {
      // Prepare account deletion data with OTP
      const deleteData = {
        whoYouAre: "user",
        helpWith: "delete_account",
        email: user?.email || "",
        otp: otp,
        reference_id: referenceId,
        reason: reason || "",
      };

      // Submit the account deletion request to the backend
      const response = await deleteAccount(deleteData);

      console.log("Account deletion response:", response.data);

      // Check if the response contains an error status
      if (response.data.status === "error") {
        console.log("Account deletion failed with error status");
        setSubmitError("Failed to delete account. Please try again.");
        return;
      }

      if (response.data.status === "success") {
        console.log("Account deletion successful");
        // Track account deletion
        UIUtils.trackButtonClick("Account Deletion Confirm");

        // Show success message
        setSubmitSuccess(true);

        // Clear auth data after a short delay to allow the user to see the success message
        // Get the API instance and clear the token
        const api = (window as any).api || {};
        if (api.clearToken) {
          api.clearToken();
        } else {
          // Fallback: clear localStorage and sessionStorage
          localStorage.removeItem("afforda_auth_token");
          localStorage.removeItem("afforda_user_data");
          sessionStorage.clear();
        }
        setTimeout(() => {
          // Redirect to home page after 5 seconds
          window.location.replace("/");
        }, 5000);
      } else {
        setSubmitError("Failed to delete account. Please try again.");
      }
    } catch (error: any) {
      console.error("Error deleting account:", error);

      // Check if it's an OTP verification error
      if (error.response?.data?.verification_status) {
        const status = error.response.data.verification_status;
        if (status === "EXPIRED") {
          setOtpError(
            "The verification code has expired. Please request a new one.",
          );
        } else if (status === "ATTEMPTS_EXHAUSTED") {
          setOtpError(
            "Too many incorrect attempts. Please request a new verification code.",
          );
        } else if (status === "INCORRECT_OTP") {
          setOtpError("Incorrect verification code. Please try again.");
        } else {
          setOtpError("Invalid verification code. Please try again.");
        }
      } else {
        setSubmitError(
          "There was an error processing your request. Please try again later.",
        );
      }

      UIUtils.trackException("Account deletion", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetDeleteAccountPopup = () => {
    setShowDeleteAccountPopup(false);
    setIsConfirmed(false);
    setIsSubmitting(false);
    setSubmitSuccess(false);
    setSubmitError("");
    setReason("");
    setShowOtpForm(false);
    setOtp("");
    setReferenceId("");
    setOtpSent(false);
    setOtpError("");
    setResendDisabled(false);
    setResendCountdown(0);
  };

  const togglePreference = async (
    title: string,
    preference: string,
    value: boolean,
  ) => {
    try {
      switch (preference) {
        case "marketingEmail":
          setIsLoading(true);
          setLoadingMessage(`Updating ${title} preference...`);
          await api.updateMarketingConsent(!value, undefined);
          setMarketingEmail(value);
          // Update UserContext with new marketing_email_dnc_flag
          updateUserData({
            user,
            userGoal,
            benefitsSummary,
            benefitsBreakdown,
            marketing_email_dnc_flag: !value,
          });
          break;
        case "marketingSms":
          setIsLoading(true);
          setLoadingMessage(`Updating ${title} preference...`);
          await api.updateMarketingConsent(undefined, !value);
          setMarketingSms(value);
          // Update UserContext with new marketing_sms_dnc_flag
          updateUserData({
            user,
            userGoal,
            benefitsSummary,
            benefitsBreakdown,
            marketing_sms_dnc_flag: !value,
          });
          break;
        case "pushNotification":
          setPushNotification(value);
          break;
        case "audioRecording":
          setAudioRecording(value);
          break;
        case "locationPermission":
          setLocationPermission(value);
          break;
      }
      // Show success message
      const notification = document.createElement("div");
      notification.className = `${styles.notification} ${styles.success}`;
      notification.textContent = `Updated ${title} preferences`;
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 3000);
    } catch (error) {
      UIUtils.trackException("Update preference", error);
      // Show error message
      const notification = document.createElement("div");
      notification.className = `${styles.notification} ${styles.error}`;
      notification.textContent = "Error updating preference. Please try again.";
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 3000);
    } finally {
      if (preference === "marketingEmail" || preference === "marketingSms") {
        setIsLoading(false);
        setLoadingMessage("");
      }
    }
  };

  const PreferenceSwitch = ({
    title,
    description,
    value,
    preference,
  }: {
    title: string;
    description: string;
    value: boolean;
    preference: string;
  }) => (
    <div className="mb-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold flex-grow">{title}</h3>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            name="preference"
            aria-label={title}
            className="sr-only peer"
            checked={value}
            onChange={(e) =>
              togglePreference(title, preference, e.target.checked)
            }
          />
          <div
            className="w-11 h-6 bg-neutral-500 peer-focus:outline-none rounded-full peer 
                        peer-checked:after:translate-x-full peer-checked:after:border-white 
                        after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                        after:bg-white after:border-gray-300 after:border after:rounded-full 
                        after:h-5 after:w-5 after:transition-all peer-checked:bg-green"
          ></div>
        </label>
      </div>
      <p
        className={`text-sm font-bold ${
          value ? "text-green-600" : "text-red-600"
        }`}
      >
        {value ? "You're opted in." : "You're opted out."}
      </p>
      <p className="text-sm mt-1">{description}</p>
    </div>
  );

  return (
    <div className="content-container">
      <PageMetadata
        title="Afforda | Manage Preferences"
        description="Manage your preferences and settings"
      />
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Loader message={loadingMessage} />
        </div>
      )}
      <div className="flex items-center justify-between pb-4 border-b">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 text-gray-600 hover:text-gray-900"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h1 className="text-3xl">Profile Settings</h1>
        </div>
      </div>
      <main className="flex-grow pt-4 max-w-4xl mx-auto w-full">
        <div className="bg-white border border-l-8 border-primary shadow-lg p-4 mb-5">
          <p className="text-base">
            Use the switches below to update your settings.
          </p>
        </div>

        {config.showMarketingEmail && (
          <PreferenceSwitch
            title="Marketing Email"
            description="Opt in to get Afforda emails. We'll send you news on discounts, loans, and offers."
            value={marketingEmail}
            preference="marketingEmail"
          />
        )}

        {config.showMarketingEmail && (
          <PreferenceSwitch
            title="Marketing SMS"
            description="Sign up for Afforda texts. We'll send you news on discounts, loans, and offers."
            value={marketingSms}
            preference="marketingSms"
          />
        )}

        {config.showPushNotification && (
          <PreferenceSwitch
            title="Push Notifications"
            description="Enable push notifications for personalised tips and reminders which may help you reach your financial goals."
            value={pushNotification}
            preference="pushNotification"
          />
        )}

        {config.showAudioRecording && (
          <PreferenceSwitch
            title="Audio Recording & Speech-to-Text"
            description="Enable voice input to describe your financial needs by speaking instead of typing, making it easier to find solutions."
            value={audioRecording}
            preference="audioRecording"
          />
        )}

        {config.showLocationPermission && (
          <PreferenceSwitch
            title="Location Permission"
            description="Allow location access to get timely reminders about savings and discounts near participating outlets, helping you make the most of Afforda's exclusive offers while shopping."
            value={locationPermission}
            preference="locationPermission"
          />
        )}
        {/* Account Deletion Section */}
        <div className="mb-8 border-b pb-6">
          <h2 className="text-xl font-bold mb-4">Account Management</h2>
          <div className="flex flex-col">
            <p className="text-sm mb-4">
              To delete your account and all your data, click the button below. This cannot be undone.
            </p>
            <div>
              <Button
                text="Delete My Account"
                onClick={() => setShowDeleteAccountPopup(true)}
                className="w-full md:w-auto"
              />
            </div>
          </div>
        </div>
      </main>

      {/* Account Deletion Popup */}
      <Popup
        isOpen={showDeleteAccountPopup}
        onClose={resetDeleteAccountPopup}
        title="Delete account"
        closeOnBackdropClick={false}
        closeOnEscape={false}
        buttons={
          submitSuccess
            ? [
                {
                  text: "Return to Homepage",
                  onClick: () => navigate("/"),
                  variant: "secondary",
                },
              ]
            : [
                {
                  text: otpSent ? "Delete" : "Continue",
                  onClick: handleDeleteAccount,
                  variant: "secondary",
                  isLoading: isSubmitting,
                  loadingText: "Processing...",
                  disabled: isSubmitting || !isConfirmed,
                },
                {
                  text: "Cancel",
                  onClick: resetDeleteAccountPopup,
                  variant: "light",
                },
              ]
        }
        className="h-[75vh]"
      >
        {submitSuccess ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-green-800 mb-2">
                Account Deleted
              </h3>
              <p className="text-green-700 mb-4">
                Your account has been successfully deleted.
              </p>
              <p className="text-green-700 mb-4">
                Thank you for using Afforda. You're welcome back any time.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="mb-2">
              We're sorry to see you go, you are welcome back anytime!
            </p>
            <div className="bg-error-lighter border border-error-light rounded-8 p-[20px]">
              <p className="font-bold mb-2">Before you go, please note:</p>
              <ul className="list-disc pl-4 text-sm">
                <li className="mb-1">
                  All your data will be permanently removed and cannot be
                  recovered.
                </li>
                <li className="mb-1">
                  You'll lose access to your saved preferences, recommendations
                  and exclusive deals.
                </li>
                <li>
                  If you decide to come back, you'll need to create a new
                  account from scratch.
                </li>
              </ul>
            </div>

            {/* Email */}
            <div>
              <p className="text-grey-80 text-sm">Email Address</p>
              <p className="text-sm">{user?.email || ""}</p>
            </div>

            {/* Reason dropdown */}
            <div>
              <label htmlFor="reason" className="block font-bold mb-4">
                Why are you leaving? (Optional)
              </label>
              <select
                id="reason"
                name="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="form-input-secondary"
              >
                <option value="">Select a reason</option>
                <option value="Found a better alternative">
                  Found a better alternative
                </option>
                <option value="No longer need financial guidance">
                  No longer need financial guidance
                </option>
                <option value="Prefer not to share personal data">
                  Prefer not to share personal data
                </option>
                <option value="Service doesn't meet my needs">
                  Service doesn't meet my needs
                </option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* OTP input field - shown after OTP is sent */}
            {showOtpForm && (
              <div>
                <label htmlFor="otp" className="block font-bold mb-4">
                  Verification Code
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="otp"
                    name="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter the 6-digit code sent to your email"
                    className="form-input-secondary"
                    maxLength={6}
                  />
                </div>
                {otpError && (
                  <p
                    className={`mt-2 text-sm ${
                      otpError.includes("resent successfully")
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {otpError}
                  </p>
                )}
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={resendDisabled}
                    className={`text-sm text-gray-500 hover:text-black ${
                      resendDisabled ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    {resendDisabled
                      ? `Resend code in ${resendCountdown}s`
                      : "Resend verification code"}
                  </button>
                </div>
              </div>
            )}

            {/* Confirmation checkbox */}
            <CustomCheckbox
              id="confirm"
              checked={isConfirmed}
              onChange={setIsConfirmed}
              label="I confirm that I want to permanently delete my account and all associated data"
              className="items-start"
            />

            {/* Error message */}
            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red">{submitError}</p>
              </div>
            )}
          </div>
        )}
      </Popup>
    </div>
  );
};

export default PreferenceCenter;
